import { Link } from "react-router-dom";
import { ArrowRight, Search, ShieldCheck, Truck } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { MOON } from "@/content/moon";
import { useLang } from "@/lib/lang";

export default function Moon() {
  const { t, lang } = useLang();
  return (
    <main data-testid="moon-page">
      <Seo
        title={lang === "fr"
          ? "MOON Supply — achat, location et recherche d'équipements | Export Monde Prestige Inc."
          : "MOON Supply — equipment purchase, rental & sourcing | Export Monde Prestige Inc."}
        description={t(MOON.header.sub)}
        path="/moon"
      />
      <section className="bg-gradient-to-b from-ice via-white to-white pb-16 pt-[152px] md:pb-20 md:pt-[200px]">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(MOON.header.eyebrow)}</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-navy-950 md:text-6xl" data-testid="moon-title">{t(MOON.header.title)}</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-graphite md:text-lg">{t(MOON.header.sub)}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/moon/recherche" data-testid="moon-cta-search" className="group inline-flex items-center gap-2 rounded-full bg-electric px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-electric-dark">
                <Search size={16} /> {t(MOON.ctaSearch)}
              </Link>
              <Link to="/catalogue" data-testid="moon-cta-quote" className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-sm font-bold text-navy-800 transition-colors duration-300 hover:border-electric hover:text-electric">
                {lang === "fr" ? "Parcourir le catalogue" : "Browse the catalogue"}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24" data-testid="moon-categories-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(MOON.categoriesTitle)}</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-4xl">{t(MOON.categoriesIntro)}</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {MOON.categories.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.04} className="h-full">
                <Link
                  to={`/moon/recherche?categorie=${c.slug}`}
                  data-testid={`moon-category-card-${c.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200/90 bg-paper p-6 transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:bg-white hover:shadow-card"
                >
                  <h3 className="font-display text-lg font-semibold text-navy-950 group-hover:text-electric">{t(c.label)}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-graphite">{t(c.desc)}</p>
                  <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-[12px] font-bold text-electric">
                    {t(MOON.ctaQuote)} <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-8 flex items-start gap-3 rounded-2xl border border-sky-200 bg-ice p-5 text-[13px] leading-relaxed text-navy-900" data-testid="moon-availability-notice">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-electric" />
              {t(MOON.notice)}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="bg-paper py-16 md:py-24" data-testid="moon-how-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(MOON.howItWorks.title)}</p>
          </Reveal>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {MOON.howItWorks.steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.07} className="h-full">
                <div className="h-full border-t-2 border-electric/40 pt-5" data-testid={`moon-step-${i}`}>
                  <span className="font-mono text-sm font-semibold text-sky-700">0{i + 1}</span>
                  <h3 className="mt-2 font-display text-xl font-semibold text-navy-950">{t(s.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-graphite">{t(s.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bande recherche rare */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-electric px-7 py-12 md:px-14 md:py-16">
              <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
              <div className="relative grid items-center gap-8 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-turquoise-bright">{t(MOON.rareBand.eyebrow)}</p>
                  <h2 className="mt-4 font-display text-3xl font-bold leading-[1.1] text-white md:text-4xl">{t(MOON.rareBand.title)}</h2>
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 md:text-base">{t(MOON.rareBand.sub)}</p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link to="/moon/recherche" data-testid="moon-rare-band-cta" className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-bold text-electric-dark transition-colors hover:bg-ice">
                    {t(MOON.rareBand.cta)} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Espace fournisseurs */}
      <section className="bg-paper py-16 md:py-20">
        <div className="mx-auto grid max-w-[1320px] items-center gap-10 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(MOON.supplierBand.eyebrow)}</p>
              <h2 className="mt-4 font-display text-3xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-4xl">{t(MOON.supplierBand.title)}</h2>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-graphite">{t(MOON.supplierBand.sub)}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Reveal delay={0.08}>
              <Link to="/fournisseurs" data-testid="moon-supplier-cta" className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-sm font-bold text-navy-800 transition-colors hover:border-electric hover:text-electric">
                <Truck size={16} /> {t(MOON.supplierBand.cta)}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand
        title={lang === "fr" ? "Décrivez votre besoin en quelques minutes." : "Describe your need in a few minutes."}
        sub={t(MOON.header.sub)}
      />
    </main>
  );
}
